import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import axios from "axios";
import SeoHead, { LOCAL_BUSINESS_SCHEMA } from "@/components/public/SeoHead";
import { useReviewStats } from "@/hooks/useReviewStats";
import Breadcrumb from "@/components/public/Breadcrumb";
import FadeIn from "@/components/public/FadeIn";
import ReviewCard from "@/components/public/ReviewCard";
import CTASection from "@/components/public/CTASection";
import NotFoundPage from "@/pages/public/NotFoundPage";
import { CITY_DATA } from "./cityData";
import { MapPin, Phone, MessageCircle, Star, ChevronDown, ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CityHubPage() {
  const location = useLocation();
  const villeSlug = location.pathname.replace("/nettoyage-", "");
  const city = CITY_DATA[villeSlug];
  const [services, setServices] = useState([]);
  const [avis, setAvis] = useState([]);
  const [seoPages, setSeoPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const reviewStats = useReviewStats();

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/services`),
      axios.get(`${API}/avis`),
      axios.get(`${API}/pages-seo`),
    ]).then(([s, a, p]) => {
      setServices(s.data);
      setAvis(a.data);
      setSeoPages(p.data.filter(pg => pg.is_active));
      setLoading(false);
    });
  }, [villeSlug]);

  if (!city) return <NotFoundPage />;
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#336DBF]" /></div>;

  const villeName = city.name;
  const villeAvis = avis.filter(a => a.ville === villeName);
  const displayAvis = villeAvis.length >= 3 ? villeAvis.slice(0, 4) : avis.slice(0, 4);
  const cityPages = seoPages.filter(p => p.ville_name === villeName || p.slug?.endsWith(`-${villeSlug}`));
  const breadcrumbItems = [{ name: "Accueil", url: "/" }, { name: `Nettoyage ${villeName}` }];

  const faqItems = [
    { q: `Combien coûte un nettoyage de canapé à ${villeName} ?`, r: `Le prix d'un nettoyage de canapé à ${villeName} commence à 125 CHF pour un canapé droit standard. Le prix varie selon la taille et l'état. Devis gratuit sous 2 heures.` },
    { q: `Nettoyage Expert intervient-il dans ${city.quartiers[0]} ?`, r: `Oui, nous intervenons à ${villeName} et dans toutes les communes environnantes : ${city.quartiers.slice(0, 5).join(", ")} et bien d'autres. Contactez-nous pour confirmer votre zone.` },
    { q: `Quel est le délai d'intervention à ${villeName} ?`, r: `Nous intervenons généralement sous 48 heures à ${villeName}. En cas d'urgence, une intervention le jour même est possible selon nos disponibilités.` },
    { q: `Proposez-vous des contrats pour les entreprises à ${villeName} ?`, r: `Oui, nous proposons des contrats de nettoyage récurrents pour les entreprises à ${villeName} : bureaux, hôtels, régies immobilières. Fréquence adaptable (hebdomadaire, mensuelle, trimestrielle).` },
    { q: `Comment obtenir un devis gratuit à ${villeName} ?`, r: `Appelez-nous au 024 539 10 33, envoyez un WhatsApp au +41 78 620 28 54, ou remplissez le formulaire sur notre site. Réponse garantie sous 2 heures, devis personnalisé et sans engagement.` },
  ];

  const faqSchema = {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": faqItems.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.r } })),
  };

  return (
    <div data-testid="city-hub-page">
      <SeoHead
        title={`Nettoyage ${villeName} – dès 125 CHF`}
        description={`Nettoyage professionnel à ${villeName} : canapés, terrasses, façades dès 125 CHF. 5★ Google. Intervention rapide. Devis gratuit en 2h. Nettoyage Expert.`}
        path={`/nettoyage-${villeSlug}`}
        breadcrumbs={breadcrumbItems}
        schemas={[faqSchema]}
      />
      <Breadcrumb items={breadcrumbItems} />

      {/* HERO */}
      <section className="bg-gradient-to-br from-[#062133] to-[#336DBF] py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 mb-5">
            <Star size={14} className="fill-amber-400 text-amber-400" />
            <span className="text-white/90 text-sm">5.0/5 sur Google • {reviewStats.count} avis • Intervention rapide</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight" data-testid="city-hub-h1">
            Entreprise de nettoyage à {villeName} – Nettoyage Expert
          </h1>
          <p className="mt-4 text-lg text-white/75 max-w-2xl mx-auto">{city.specificity}</p>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <FadeIn>
            <p className="text-[#062133]/80 text-base sm:text-lg leading-relaxed">{city.intro}</p>
            <p className="mt-4 text-[#062133]/80 text-base sm:text-lg leading-relaxed">{city.specificity}</p>
            <p className="mt-4 text-[#336DBF] font-semibold">Noté 5 étoiles sur Google avec {reviewStats.count} avis clients vérifiés.</p>
          </FadeIn>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-12 sm:py-16 bg-[#F4F6F8]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <FadeIn>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#062133] tracking-tight mb-8">Nos services à {villeName}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map(s => {
                const seoSlug = `${s.slug}-${villeSlug}`;
                const seoPage = cityPages.find(p => p.slug === seoSlug);
                const linkTo = seoPage ? `/${seoSlug}` : `/services/${s.slug}`;
                return (
                  <Link key={s.slug} to={linkTo} className="bg-white border border-[#062133]/10 rounded-xl p-5 hover:border-[#336DBF]/30 hover:shadow-md transition-all group" data-testid={`city-service-${s.slug}`}>
                    <h3 className="font-semibold text-[#062133] group-hover:text-[#336DBF] transition-colors">{s.name}</h3>
                    <p className="text-sm text-[#062133]/50 mt-1">{s.prix_moyen}</p>
                    <span className="inline-flex items-center gap-1 text-xs text-[#336DBF] mt-2 font-medium">En savoir plus <ArrowRight size={12} /></span>
                  </Link>
                );
              })}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* QUARTIERS */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <FadeIn>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#062133] tracking-tight mb-6">Zones desservies à {villeName}</h2>
            <p className="text-[#062133]/60 mb-6">Nous intervenons dans toute l'agglomération de {villeName} et ses environs :</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {city.quartiers.map(q => (
                <div key={q} className="flex items-center gap-2 bg-[#F4F6F8] rounded-lg px-4 py-3 text-sm text-[#062133]">
                  <MapPin size={14} className="text-[#336DBF] shrink-0" /> {q}
                </div>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* AVIS */}
      <section className="py-12 sm:py-16 bg-[#F4F6F8]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <FadeIn>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#062133] tracking-tight mb-8">Avis clients {villeAvis.length >= 3 ? `à ${villeName}` : "en Suisse romande"}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {displayAvis.map((a, i) => <ReviewCard key={a.id || i} avis={a} />)}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <FadeIn>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#062133] tracking-tight mb-8">Questions fréquentes – Nettoyage à {villeName}</h2>
            <div className="space-y-3">
              {faqItems.map((item, i) => (
                <details key={i} className="group bg-[#F4F6F8] border border-[#062133]/10 rounded-lg" data-testid={`hub-faq-item-${i}`}>
                  <summary className="flex items-center justify-between cursor-pointer p-5 list-none [&::-webkit-details-marker]:hidden">
                    <span className="font-medium text-[#062133] pr-4">{item.q}</span>
                    <ChevronDown className="w-5 h-5 text-[#062133]/60 shrink-0 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="px-5 pb-5 pt-0"><p className="text-[#062133]/60 leading-relaxed">{item.r}</p></div>
                </details>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* CTA */}
      <CTASection title={`Demandez votre devis gratuit à ${villeName}`} subtitle="Réponse garantie sous 2 heures. Sans engagement." />
    </div>
  );
}
